// qchat_fe/src/Component/SideData/Overview/AiAgentWorkFlow/triggerEligibility.js

const SITE_TO_CANONICAL = {
  text: "string", email: "string", tel: "string", textarea: "string",
  select: "string", radio: "string", number: "number", range: "number",
  checkbox: "array<string>", toggle: "boolean", unknown: "unknown",
  
  date: "date", 
  time: "time", 
  "datetime-local": "datetime", 
  datetime: "datetime",

  string: "string", integer: "integer", float: "float", boolean: "boolean",
  "array<string>": "array<string>", "array<object>": "array<object>", object: "object",
};

export function canonicalTypeOf(siteType) {
  if (!siteType) return "unknown";
  return SITE_TO_CANONICAL[siteType] || "unknown";
}

const CONVERT_TO_MATRIX = {
  "string->string": null, "string->number": "number", "string->integer": "integer",
  "string->boolean": "boolean", "string->array<string>": "array",
  "string->array<object>": "json", "string->object": "json",
  "string->date": "date", "string->time": "time", "string->datetime": "datetime",

  "number->string": "string", "number->number": null, "number->integer": "integer",
  "number->boolean": "boolean", "number->array<string>": "array", "number->date": "date",

  "boolean->string": "string", "boolean->number": "number", "boolean->integer": "integer",
  "boolean->boolean": null, "boolean->array<string>": "array",

  "array<string>->string": "string", "array<string>->array<string>": null,
  "array<string>->array<object>": "json_array",
  
  // ⭐ Date and time conversions (Strict)
  "date->string": "string", 
  "date->date": null, 
  // "date->datetime": undefined, ⭐ BLOCKED: Cannot guess time (midnight)
  
  "time->string": "string", 
  "time->time": null,
  // "time->datetime": undefined, ⭐ BLOCKED: Cannot guess date
  
  "datetime->string": "string", 
  "datetime->date": "date", // ⭐ ALLOWED: Dropping time is safe
  "datetime->datetime": null,
  
  "array<object>->array<object>": null, "object->object": null,
};

export function conversionFor(fromCanonical, toCanonical) {
  if (!fromCanonical || !toCanonical) return undefined;
  if (fromCanonical === toCanonical) return null;
  return CONVERT_TO_MATRIX[`${fromCanonical}->${toCanonical}`];
}

export function prettyCanonicalType(t) {
  switch (t) {
    case "array<string>": return "list of strings";
    case "array<object>": return "list of objects";
    case "date": return "date";
    case "time": return "time";
    case "datetime": return "date & time";
    default: return t || "unknown";
  }
}

function extractTemplateVars(templateStr) {
  if (typeof templateStr !== "string") return [];
  const matches = templateStr.match(/\{\{\s*([^}]+?)\s*\}\}/g) || [];
  return [...new Set(matches.map(m => m.replace(/[\{\}\s]/g, "").trim()))];
}

function inferVariableType(varName, fallbackType) {
  const n = varName.toLowerCase();
  if (n.includes("availability") || n.includes("datetime") || (n.includes("date") && n.includes("time"))) return "datetime"; 
  if (n.includes("date")) return "date";
  if (n.includes("time")) return "time";
  if (n.includes("email")) return "email";
  if (n.includes("phone") || n.includes("tel")) return "tel";
  if (n.includes("price") || n.includes("amount")) return "number";
  return fallbackType;
}

function nameScore(targetName, candidateName) {
  const t = String(targetName || "").toLowerCase();
  const c = String(candidateName || "").toLowerCase();
  if (!t || !c) return 0;
  if (t === c) return 100;
  if (t.includes(c) || c.includes(t)) return 60;
  return 0;
}

export function classifyField(requiredField, siteFields) {
  const key = requiredField.key;
  const targetType = requiredField.type || "string";
  const targetCanonical = canonicalTypeOf(targetType);
  const fields = Array.isArray(siteFields) ? siteFields : [];

  const exactDirect = fields.find(
    (f) => f.key === key && canonicalTypeOf(f.type) === targetCanonical
  );
  if (exactDirect) {
    return {
      field: key, targetType, bucket: "auto",
      source: `{{ ${exactDirect.key} }}`, convert_to: null,
      candidates: [exactDirect], reason: `Mapped to "${exactDirect.key}"`,
    };
  }

  const convertible = [];
  for (const f of fields) {
    const from = canonicalTypeOf(f.type);
    if (from === "unknown") continue;
    if (conversionFor(from, targetCanonical) !== undefined) convertible.push(f);
  }

  if (convertible.length > 0) {
    const ranked = [...convertible].sort(
      (a, b) => nameScore(key, b.key) - nameScore(key, a.key)
    );
    const chosen = ranked[0];
    const from = canonicalTypeOf(chosen.type);
    const conv = conversionFor(from, targetCanonical);
    const score = nameScore(key, chosen.key);
    const source = score > 0 ? `{{ ${chosen.key} }}` : "";

    return {
      field: key, targetType, bucket: "map",
      source, convert_to: source ? conv : null, candidates: ranked,
      reason: score > 0
        ? `Auto-matched "${chosen.key}"`
        : `Select a source for "${key}"`,
    };
  }

  return {
    field: key, targetType, bucket: "override",
    source: null, convert_to: null, candidates: fields,
    reason: `No compatible field found`,
  };
}

export function classifyAttachment(definition, siteFields, connectedProviders = []) {
  const op = definition || {};
  const provider = (op.operation_name || "").split(".")[0];

  if (provider && Array.isArray(connectedProviders) && !connectedProviders.includes(provider)) {
    return { bucket: "blocked", fieldResults: [], reason: `Provider "${provider}" is not connected` };
  }

  const schema = op.input_schema || {};
  const operationParams = op.operation_params || {};

  const allVars = new Map();

  for (const [opKey, spec] of Object.entries(schema)) {
    const isRequired = spec.required === true;
    const fallbackType = spec.type || "string";
    const templateVal = operationParams[opKey];

    if (typeof templateVal === "string") {
      const vars = extractTemplateVars(templateVal);
      for (const v of vars) {
        const existing = allVars.get(v);
        allVars.set(v, {
          isRequired: isRequired || (existing && existing.isRequired),
          targetType: inferVariableType(v, fallbackType),
        });
      }
    }
  }

  if (allVars.size === 0) {
    return { bucket: "ready", fieldResults: [], reason: "No template variables found" };
  }

  const fieldResults = Array.from(allVars.entries()).map(([varName, meta]) => {
    const result = classifyField({ key: varName, type: meta.targetType }, siteFields);
    return { ...result, isRequired: meta.isRequired };
  });

  return { bucket: "needs-mapping", fieldResults, reason: `Review required template variables` };
}

export function sortIntoBuckets(definitions, siteFields, connectedProviders = []) {
  const ready = [];
  const needsMapping = [];
  const blocked = [];

  for (const def of definitions || []) {
    const classification = classifyAttachment(def, siteFields, connectedProviders);
    const entry = { definition: def, classification };
    if (classification.bucket === "ready") ready.push(entry);
    else if (classification.bucket === "needs-mapping") needsMapping.push(entry);
    else blocked.push(entry);
  }

  const sortByLabel = (a, b) =>
    (a.definition.name || "").localeCompare(b.definition.name || "");
  ready.sort(sortByLabel);
  needsMapping.sort(sortByLabel);
  blocked.sort(sortByLabel);

  return { ready, needsMapping, blocked };
}

export function buildAttachmentFromClassification(connectorDefinitionId, classification) {
  const attachment = { connector_definition_id: connectorDefinitionId };
  const overrides = {};

  for (const r of classification.fieldResults || []) {
    if (r.bucket === "auto" || !r.source) continue;
    overrides[r.field] = { source: r.source };
    if (r.convert_to) overrides[r.field].convert_to = r.convert_to;
  }

  if (Object.keys(overrides).length > 0) attachment.field_overrides = overrides;
  return attachment;
}